import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'

@customElement('schmancy-typography')
export class SchmancyTypography extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				font-family: inherit;

				/* Inherited, never a value of its own: hyphens inherits, and a concrete
				   value declared here outranks whatever an ancestor sets, so the former
				   none swallowed a consumer's own hyphens: auto and even an explicit
				   &shy;. Inheriting resolves to the UA initial (manual) when no ancestor
				   speaks, so no text in the library starts hyphenating on its own, while
				   a consumer that opts in — hyphens: auto plus a lang attribute — now
				   reaches the text it is styling. */
				hyphens: inherit;

				/* Interpolation term for the display and headline scales below: 0 at a
				   320px viewport, 1 at 600px, past 1 above it. Fixed px sizes overflowed
				   their column on a phone — at 320px a 288px content column cannot hold
				   "Datenschutzerklärung" at display/sm's 36px, where the word measures
				   316px and breaks mid-word. 600px ends the ramp because it is Material's
				   compact-window boundary, so every viewport from tablet up keeps the
				   exact px scale it had. Held here once rather than inlined per token: a
				   mistyped divisor is invisible at both ends of the ramp, which clamp
				   pins to the min and max whatever the slope in between. Viewport units
				   and not a container query, which would need the consumer to declare
				   container-type — something a text primitive cannot require. */
				--_fluid-ramp: (100vw - 320px) / 280;
			}

			:host([align='center']) {
				text-align: center;
			}

			:host([align='left']) {
				text-align: start;
			}

			:host([align='right']) {
				text-align: right;
			}

			:host([align='justify']) {
				text-align: justify;
			}

			:host([weight='bold']) {
				font-weight: 700;
			}

			:host([weight='medium']) {
				font-weight: 500;
			}

			:host([weight='normal']) {
				font-weight: 400;
			}

			:host([transform='uppercase']) {
				text-transform: uppercase;
			}

			:host([transform='lowercase']) {
				text-transform: lowercase;
			}

			:host([transform='capitalize']) {
				text-transform: capitalize;
			}

			:host([transform='normal']) {
				text-transform: none;
			}

			/* Per-type default weight. The :not([weight]) guard is load-bearing,
			   not decoration: these rules are :host plus one attribute selector
			   (0,2,0), exactly the weight prop's own weight, so at any source
			   position one of the two silently wins for every element — an
			   explicit weight= wins only by these defaults declining to match.
			   title at xl/lg is the one size whose weight departs from its
			   type's default, so it is carried here rather than in the size
			   rules below, which would outrank the weight prop at (0,3,0). */
			:host([type='display']:not([weight])),
			:host([type='headline']:not([weight])),
			:host([type='body']:not([weight])),
			:host([type='title'][token='xl']:not([weight])),
			:host([type='title'][token='lg']:not([weight])) {
				font-weight: 400;
			}

			:host([type='label']:not([weight])),
			:host([type='subtitle']:not([weight])),
			:host([type='title']:not([weight])) {
				font-weight: 500;
			}

			/* Every rung reads its size and line-height off the typescale token
			   family, each spelled with the literal the token carries by
			   default: the --schmancy-sys-typescale-* channel is unset until an
			   app declares it, and an unset var() with no fallback is
			   invalid-at-computed-value-time — the rule would render at the
			   inherited size, not at this rung's. Same shape the radius ladder
			   uses. The family is registered in packages/theme/src/theme.style.css
			   and packages/mixins/src/tailwind.css.

			   Display and headline spell that fallback as a clamp() ramp rather
			   than a bare literal, so the library's own default shrinks on a
			   phone while an app that declares the token still gets exactly the
			   value it declared — the token channel stays authoritative, and the
			   ramp is only what the library falls back to. Every clamp maximum
			   is the literal the rung has always carried, so the two agree from
			   600px up. The other four families sit at or under 24px already,
			   which clears a 288px column for a 20-character German compound, so
			   shrinking them would cost legibility to fix nothing. */
			:host([type='display'][token='xl']) {
				font-size: var(--schmancy-sys-typescale-display-xl-size, clamp(40px, calc(40px + 32 * var(--_fluid-ramp)), 72px));
				line-height: var(--schmancy-sys-typescale-display-xl-line-height, clamp(44px, calc(44px + 36 * var(--_fluid-ramp)), 80px));
			}

			:host([type='display'][token='lg']) {
				font-size: var(--schmancy-sys-typescale-display-lg-size, clamp(34px, calc(34px + 23 * var(--_fluid-ramp)), 57px));
				line-height: var(--schmancy-sys-typescale-display-lg-line-height, clamp(40px, calc(40px + 24 * var(--_fluid-ramp)), 64px));
			}

			:host([type='display'][token='md']) {
				font-size: var(--schmancy-sys-typescale-display-md-size, clamp(30px, calc(30px + 15 * var(--_fluid-ramp)), 45px));
				line-height: var(--schmancy-sys-typescale-display-md-line-height, clamp(36px, calc(36px + 16 * var(--_fluid-ramp)), 52px));
			}

			:host([type='display'][token='sm']) {
				font-size: var(--schmancy-sys-typescale-display-sm-size, clamp(26px, calc(26px + 10 * var(--_fluid-ramp)), 36px));
				line-height: var(--schmancy-sys-typescale-display-sm-line-height, clamp(32px, calc(32px + 12 * var(--_fluid-ramp)), 44px));
			}

			:host([type='display'][token='xs']) {
				font-size: var(--schmancy-sys-typescale-display-xs-size, clamp(22px, calc(22px + 6 * var(--_fluid-ramp)), 28px));
				line-height: var(--schmancy-sys-typescale-display-xs-line-height, clamp(28px, calc(28px + 8 * var(--_fluid-ramp)), 36px));
			}

			:host([type='headline'][token='xl']) {
				font-size: var(--schmancy-sys-typescale-headline-xl-size, clamp(26px, calc(26px + 10 * var(--_fluid-ramp)), 36px));
				line-height: var(--schmancy-sys-typescale-headline-xl-line-height, clamp(32px, calc(32px + 12 * var(--_fluid-ramp)), 44px));
			}

			:host([type='headline'][token='lg']) {
				font-size: var(--schmancy-sys-typescale-headline-lg-size, clamp(24px, calc(24px + 8 * var(--_fluid-ramp)), 32px));
				line-height: var(--schmancy-sys-typescale-headline-lg-line-height, clamp(30px, calc(30px + 10 * var(--_fluid-ramp)), 40px));
			}

			:host([type='headline'][token='md']) {
				font-size: var(--schmancy-sys-typescale-headline-md-size, clamp(22px, calc(22px + 6 * var(--_fluid-ramp)), 28px));
				line-height: var(--schmancy-sys-typescale-headline-md-line-height, clamp(28px, calc(28px + 8 * var(--_fluid-ramp)), 36px));
			}

			:host([type='headline'][token='sm']) {
				font-size: var(--schmancy-sys-typescale-headline-sm-size, clamp(20px, calc(20px + 4 * var(--_fluid-ramp)), 24px));
				line-height: var(--schmancy-sys-typescale-headline-sm-line-height, clamp(26px, calc(26px + 6 * var(--_fluid-ramp)), 32px));
			}

			:host([type='headline'][token='xs']) {
				font-size: var(--schmancy-sys-typescale-headline-xs-size, clamp(18px, calc(18px + 2 * var(--_fluid-ramp)), 20px));
				line-height: var(--schmancy-sys-typescale-headline-xs-line-height, clamp(24px, calc(24px + 4 * var(--_fluid-ramp)), 28px));
			}

			:host([type='title'][token='xl']) {
				font-size: var(--schmancy-sys-typescale-title-xl-size, 24px);
				line-height: var(--schmancy-sys-typescale-title-xl-line-height, 32px);
			}

			:host([type='title'][token='lg']) {
				font-size: var(--schmancy-sys-typescale-title-lg-size, 22px);
				line-height: var(--schmancy-sys-typescale-title-lg-line-height, 28px);
			}

			:host([type='title'][token='md']) {
				font-size: var(--schmancy-sys-typescale-title-md-size, 16px);
				line-height: var(--schmancy-sys-typescale-title-md-line-height, 24px);
			}

			:host([type='title'][token='sm']) {
				font-size: var(--schmancy-sys-typescale-title-sm-size, 14px);
				line-height: var(--schmancy-sys-typescale-title-sm-line-height, 20px);
			}

			:host([type='title'][token='xs']) {
				font-size: var(--schmancy-sys-typescale-title-xs-size, 12px);
				line-height: var(--schmancy-sys-typescale-title-xs-line-height, 16px);
			}

			:host([type='subtitle'][token='xl']) {
				font-size: var(--schmancy-sys-typescale-subtitle-xl-size, 20px);
				line-height: var(--schmancy-sys-typescale-subtitle-xl-line-height, 28px);
			}

			:host([type='subtitle'][token='lg']) {
				font-size: var(--schmancy-sys-typescale-subtitle-lg-size, 18px);
				line-height: var(--schmancy-sys-typescale-subtitle-lg-line-height, 24px);
			}

			:host([type='subtitle'][token='md']) {
				font-size: var(--schmancy-sys-typescale-subtitle-md-size, 16px);
				line-height: var(--schmancy-sys-typescale-subtitle-md-line-height, 24px);
			}

			:host([type='subtitle'][token='sm']) {
				font-size: var(--schmancy-sys-typescale-subtitle-sm-size, 14px);
				line-height: var(--schmancy-sys-typescale-subtitle-sm-line-height, 20px);
			}

			:host([type='subtitle'][token='xs']) {
				font-size: var(--schmancy-sys-typescale-subtitle-xs-size, 12px);
				line-height: var(--schmancy-sys-typescale-subtitle-xs-line-height, 16px);
			}

			:host([type='body'][token='xl']) {
				font-size: var(--schmancy-sys-typescale-body-xl-size, 18px);
				line-height: var(--schmancy-sys-typescale-body-xl-line-height, 28px);
			}

			:host([type='body'][token='lg']) {
				font-size: var(--schmancy-sys-typescale-body-lg-size, 16px);
				line-height: var(--schmancy-sys-typescale-body-lg-line-height, 24px);
			}

			:host([type='body'][token='md']) {
				font-size: var(--schmancy-sys-typescale-body-md-size, 14px);
				line-height: var(--schmancy-sys-typescale-body-md-line-height, 20px);
			}

			:host([type='body'][token='sm']) {
				font-size: var(--schmancy-sys-typescale-body-sm-size, 12px);
				line-height: var(--schmancy-sys-typescale-body-sm-line-height, 16px);
			}

			:host([type='body'][token='xs']) {
				font-size: var(--schmancy-sys-typescale-body-xs-size, 10px);
				line-height: var(--schmancy-sys-typescale-body-xs-line-height, 14px);
			}

			:host([type='label'][token='xl']) {
				font-size: var(--schmancy-sys-typescale-label-xl-size, 16px);
				line-height: var(--schmancy-sys-typescale-label-xl-line-height, 22px);
			}

			:host([type='label'][token='lg']) {
				font-size: var(--schmancy-sys-typescale-label-lg-size, 14px);
				line-height: var(--schmancy-sys-typescale-label-lg-line-height, 20px);
			}

			:host([type='label'][token='md']) {
				font-size: var(--schmancy-sys-typescale-label-md-size, 12px);
				line-height: var(--schmancy-sys-typescale-label-md-line-height, 16px);
			}

			:host([type='label'][token='sm']) {
				font-size: var(--schmancy-sys-typescale-label-sm-size, 11px);
				line-height: var(--schmancy-sys-typescale-label-sm-line-height, 16px);
			}

			:host([type='label'][token='xs']) {
				font-size: var(--schmancy-sys-typescale-label-xs-size, 10px);
				line-height: var(--schmancy-sys-typescale-label-xs-line-height, 14px);
			}

			/* Custom letter-spacing, font-size, or line-height: use inline styles or
			   Tailwind classes — this component exposes no prop for them. */
		`,
	]
	static shadowRootOptions: ShadowRootInit = {
		mode: 'open',
		delegatesFocus: true,
	}

	@property({ type: String, reflect: true })
	type: 'display' | 'headline' | 'title' | 'subtitle' | 'body' | 'label' = 'body'

	/** @deprecated Prefer Tailwind responsive text classes: token="" class="text-sm md:text-base lg:text-lg" */
	@property({ type: String, reflect: true })
	token: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '' = 'md'

	// Unset properties below fall back to the inherited CSS value, not a JS default.
	@property({ type: String, reflect: true })
	align: 'left' | 'center' | 'justify' | 'right' | undefined

	@property({ type: String, reflect: true })
	weight: 'normal' | 'medium' | 'bold' | undefined

	@property({ type: String, reflect: true })
	transform: 'uppercase' | 'lowercase' | 'capitalize' | 'normal' | undefined

	@property({ type: Number })
	maxLines: 1 | 2 | 3 | 4 | 5 | 6 | undefined

	protected updated(changedProperties: Map<string, unknown>): void {
		super.updated(changedProperties)
		if (changedProperties.has('maxLines')) {
			this.classList.remove(
				'line-clamp-1',
				'line-clamp-2',
				'line-clamp-3',
				'line-clamp-4',
				'line-clamp-5',
				'line-clamp-6',
			)
			if (this.maxLines) {
				this.classList.add(`line-clamp-${this.maxLines}`)
			}
		}
	}

	protected render(): unknown {
		return html`<slot></slot>`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-typography': SchmancyTypography
	}
}
